import dayjs from "dayjs";
import { Key, useMemo, useRef, useState } from "react";
import { DateFormatType, DateFormatter } from "types/common";
import { getLocalStorage, setLocalStorage } from "utils/localStorageUtils";

export default function useDateFormats({
  prefixKey,
  defaultFormat = "fullDate",
  defaultToKeys,
}: {
  prefixKey: string;
  defaultFormat?: DateFormatType;
  defaultToKeys?: Record<Key, DateFormatType>;
}) {
  const defaultRef = useRef(defaultToKeys);
  const [dateFormat, setDateFormat] = useState<Record<string, DateFormatType>>(
    () => getFormat(prefixKey),
  );

  const dateFormatter = useMemo<DateFormatter>(() => {
    const getFormat: DateFormatter["getFormat"] = (key) =>
      dateFormat[key] || defaultRef.current?.[key] || defaultFormat;
    return {
      getFormat,
      setFormat: (key, format) => {
        setDateFormat((prev) => {
          const formats = { ...prev, [key]: format };
          setFormat(prefixKey, formats);
          return formats;
        });
      },
      render: (key, value) => {
        if (!value) {
          return "N/A";
        }
        const format = getFormat(key);
        if (format === "date") {
          return dayjs(value).format(DateFormatStrings.DATE);
        } else if (format === "datetime") {
          return dayjs(value).format(DateFormatStrings.DATE_TIME);
        } else if (format === "relativeTime") {
          return dayjs(value).fromNow();
        }
        // fullDate
        return dayjs(value).format(DateFormatStrings.FULL_DATE);
      },
    };
  }, [dateFormat, defaultFormat, prefixKey]);

  return dateFormatter;
}

const getFormat = (key: string): Record<string, DateFormatType> => {
  return getLocalStorage(`${key}_date_format`, {});
};
const setFormat = (key: string, config: Record<string, DateFormatType>) => {
  return setLocalStorage(`${key}_date_format`, config);
};

export const DateFormatStrings = {
  DATE: "MMMM DD, YYYY",
  DATE_TIME: "MMMM DD, YYYY - h:mm A",
  FULL_DATE: "YYYY/MM/DD - h:mm A",
};
